import { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { SEOSchemasContainer_feedContentResource$key } from "@framework/__generated__/relay/SEOSchemasContainer_feedContentResource.graphql";
import { FEED_CONTENT_RESOURCE_TYPES } from "@framework/config/wordpress.config";
import {
  advertiserContentArticleSchema,
  breadcrumbListSchema,
  itemListProductSchema,
  liveBlogPostingSchema,
  newsArticleSchema,
  productObjectSchema,
  publisherSchema,
  videoObjectSchema,
} from "@framework/seo/seo.utils";
import { FeedContentResourceTypename } from "@framework/types/wordpress";
import { getBreadcrumbCategories } from "@framework/utils/category.utils";
import { useConfigContext } from "@voxmedia/duet-context/context/config-context";
import { useGlobalDataContext } from "@voxmedia/duet-context/context/global-data-context";
import constructEmbedUrl from "@voxmedia/duet-utils/video/construct-embed-url";
import getVolumeEmbedHost from "@voxmedia/duet-utils/video/get-volume-embed-host";

import SEOSchema from "./SEOSchema";

interface Props {
  feedContentResource: SEOSchemasContainer_feedContentResource$key;
}

const SEOSchemasContainer: FunctionComponent<Props> = (props) => {
  const node = useFragment(
    graphql`
      fragment SEOSchemasContainer_feedContentResource on FeedContentResourceInterface {
        __typename

        title

        seo {
          description
          headline
        }

        dek {
          plaintext
        }

        originalPublishedAt
        publishedAt
        permalink
        isPaywallEligible

        liveStatus {
          status
          startAt
          endAt
        }

        # @excludeCacheKey: assignment/unassignment of authors will result in invalidation of the
        # resource cache key, so including the key is not needed
        authors @excludeCacheKey {
          name
          permalink
          title
          profileImage {
            thumbnails {
              square {
                url
              }
            }
          }
        }

        seoArticleBody

        primaryCampaign {
          id
        }

        # @excludeCacheKey: assignment/unassignment of primary category will result in invalidation
        # of the resource cache key, so including the key is not needed
        primaryCategory @excludeCacheKey {
          title
          permalink
          # @excludeCacheKey: parent categories of a primary category are added to a resource
          # automatically, and the addition or removal of these categories via the primary
          # category changing will result in the invalidation of the resource cache key
          parent @excludeCacheKey {
            title
            permalink
            parent @excludeCacheKey {
              title
              permalink
            }
          }
        }

        # @excludeCacheKey: assignment/unassignment of categories will result in invalidation
        # of the resource cache key, so including the key is not needed
        categories @excludeCacheKey {
          title
          permalink
          parent @excludeCacheKey {
            title
            permalink
            parent @excludeCacheKey {
              title
              permalink
            }
          }
        }

        blocks {
          __typename

          ... on ProductBlockType {
            product {
              title
              score
              showScores
              brand {
                title
              }
              retailLinks {
                price
                retailer
                salePrice
                url
              }
              image {
                thumbnails {
                  horizontal {
                    url
                  }
                }
              }
            }
          }

          ... on LegacyProductBlockType {
            legacyProduct {
              title
              score
              brand
              retailLinks {
                price
                retailer
                salePrice
                url
              }
              image {
                thumbnails {
                  horizontal {
                    url
                  }
                }
              }
            }
          }

          ... on ProductsTableBlockType {
            products {
              title
              score
              showScores
              brand {
                title
              }
              retailLinks {
                price
                retailer
                salePrice
                url
              }
              image {
                thumbnails {
                  horizontal {
                    url
                  }
                }
              }
            }
          }

          ... on LegacyProductsTableBlockType {
            legacyProducts {
              title
              score
              brand
              retailLinks {
                price
                retailer
                salePrice
                url
              }
              image {
                thumbnails {
                  horizontal {
                    url
                  }
                }
              }
            }
          }

          ... on VideoBlockType {
            autoplay
            caption {
              plaintext
            }

            video {
              volumeUuid
              title
              player
              publishedAt
              poster {
                thumbnails {
                  horizontal {
                    url
                  }
                }
              }
            }
          }
        }

        ... on StreamResourceType {
          streamPublishedAt
          streamPosts: posts(first: 100) {
            # @excludeCacheKey: assignment/unassignment of the stream shadow taxonomy will result in
            # invalidation of the resource cache key, so including the key is not needed
            nodes @excludeCacheKey {
              ... on FeedContentResourceInterface {
                title

                seo {
                  description
                  headline
                }

                dek {
                  plaintext
                }

                originalPublishedAt
                publishedAt
                permalink
                isPaywallEligible

                authors @excludeCacheKey {
                  name
                  permalink
                  title
                  profileImage {
                    thumbnails {
                      square {
                        url
                      }
                    }
                  }
                }

                primaryCategory @excludeCacheKey {
                  title
                }

                # @excludeCacheKey: assignment/unassignment of categories will result in invalidation
                # of the resource cache key, so including the key is not needed
                categories @excludeCacheKey {
                  title
                  permalink
                  parent @excludeCacheKey {
                    title
                    permalink
                    parent @excludeCacheKey {
                      title
                      permalink
                    }
                  }
                }

                ... on PostResourceType {
                  excerpt {
                    paragraphContents {
                      plaintext
                    }
                  }
                }

                ... on QuickPostResourceType {
                  quickAttachment {
                    __typename
                    ... on ProductBlockType {
                      product {
                        title
                        score
                        showScores
                        brand {
                          title
                        }
                        retailLinks {
                          price
                          retailer
                          salePrice
                          url
                        }
                        image {
                          thumbnails {
                            horizontal {
                              url
                            }
                          }
                        }
                      }
                    }
                  }
                }

                # TODO [WPSCHEMA]: Ideally we should be able to source this from 'seo' but that is not auto-populated right now.
                ledeMedia {
                  __typename

                  ... on LedeMediaImageType {
                    image {
                      originalWidth
                      originalHeight
                      thumbnails {
                        social {
                          url
                          cropCoordinates {
                            height
                            width
                          }
                        }
                        horizontal {
                          url
                          cropCoordinates {
                            height
                            width
                          }
                        }
                        square {
                          url
                          cropCoordinates {
                            height
                            width
                          }
                        }
                      }
                    }
                  }

                  ... on LedeMediaVideoType {
                    autoplay
                    caption {
                      plaintext
                    }

                    video {
                      volumeUuid
                      title
                      player
                      publishedAt
                      poster {
                        thumbnails {
                          horizontal {
                            url
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }

        # TODO [WPSCHEMA]: Ideally we should be able to source this from 'seo' but that is not auto-populated right now.
        ledeMedia {
          __typename

          ... on LedeMediaImageType {
            image {
              originalWidth
              originalHeight
              thumbnails {
                social {
                  url
                  cropCoordinates {
                    height
                    width
                  }
                }
                horizontal {
                  url
                  cropCoordinates {
                    height
                    width
                  }
                }
                square {
                  url
                  cropCoordinates {
                    height
                    width
                  }
                }
              }
            }
          }

          ... on LedeMediaVideoType {
            autoplay
            caption {
              plaintext
            }

            video {
              volumeUuid
              title
              player
              publishedAt
              poster {
                thumbnails {
                  horizontal {
                    url
                  }
                }
              }
            }
          }
        }

        ... on QuickPostResourceType {
          quickAttachment {
            __typename
            ... on ProductBlockType {
              product {
                title
                score
                showScores
                brand {
                  title
                }
                retailLinks {
                  price
                  retailer
                  salePrice
                  url
                }
                image {
                  thumbnails {
                    horizontal {
                      url
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
    props.feedContentResource,
  );

  const { communityConfig, featureConfig } = useConfigContext();
  const { siteLogo } = useGlobalDataContext().siteSettings;

  if (
    !FEED_CONTENT_RESOURCE_TYPES.includes(
      node.__typename as FeedContentResourceTypename,
    )
  ) {
    return;
  }

  const publisher = publisherSchema(
    communityConfig.communityName,
    siteLogo?.social || null,
  );

  // Use the brand's specific breadcrumb strategy for articles
  const { breadcrumbStrategy } = featureConfig;
  const breadcrumbCategories = getBreadcrumbCategories(
    node.primaryCategory,
    3,
    [...node.categories],
    node.__typename === "StreamResourceType" ? "hierarchy" : breadcrumbStrategy,
  );

  const schemas = [];

  if (node.liveStatus.startAt) {
    schemas.push(liveBlogPostingSchema(node, publisher, breadcrumbCategories));
  }

  if (node.primaryCampaign) {
    // This is advertiser content
    schemas.push(
      advertiserContentArticleSchema(node, publisher, breadcrumbCategories),
    );
  } else {
    schemas.push(newsArticleSchema(node, publisher, breadcrumbCategories));
  }

  // Do not add breadcrumb schema to packages, live streams, or ad content. (None show
  // the breadcrumbs in the UI, and we should only include this metadata when
  // breadcrumbs appear to users.) This logic is fragile since the design
  // could change.
  if (
    node.__typename !== "PackageResourceType" &&
    node.__typename !== "QuickPostResourceType" &&
    !node.liveStatus.status &&
    !node.primaryCampaign
  ) {
    schemas.push(breadcrumbListSchema(breadcrumbCategories));
  }

  const volumeEmbedHost = getVolumeEmbedHost(communityConfig.communityDomain);

  if (node.ledeMedia?.__typename === "LedeMediaVideoType") {
    const embedUrl = constructEmbedUrl({
      volumeEmbedHost,
      uuid: node.ledeMedia.video.volumeUuid,
      autoplay: node.ledeMedia.autoplay,
      playerType: node.ledeMedia.video.player,
    });
    schemas.push(videoObjectSchema(node.ledeMedia, embedUrl));
  }

  node.blocks.forEach((block) => {
    if (block.__typename === "VideoBlockType" && block.video?.volumeUuid) {
      const embedUrl = constructEmbedUrl({
        volumeEmbedHost,
        uuid: block.video.volumeUuid,
        autoplay: block.autoplay,
        playerType: block.video.player,
      });
      schemas.push(videoObjectSchema(block, embedUrl));
    } else if (block.__typename === "ProductBlockType") {
      schemas.push(productObjectSchema(block.product, node.authors));
    } else if (block.__typename === "LegacyProductBlockType") {
      const { brand, ...legacyProduct } = block.legacyProduct;
      schemas.push(
        productObjectSchema(
          {
            brand: brand.length > 0 ? { title: brand } : null,
            showScores: !!block.legacyProduct.score,
            ...legacyProduct,
          },
          node.authors,
        ),
      );
    } else if (block.__typename === "ProductsTableBlockType") {
      schemas.push(itemListProductSchema(block.products, node.authors));
    } else if (block.__typename === "LegacyProductsTableBlockType") {
      schemas.push(
        itemListProductSchema(
          block.legacyProducts.map((lp) => {
            const { brand, ...legacyProduct } = lp;
            return {
              brand: brand.length > 0 ? { title: brand } : null,
              showScores: !!legacyProduct.score,
              ...legacyProduct,
            };
          }),
          node.authors,
        ),
      );
    }
  });

  if (node.quickAttachment?.__typename === "ProductBlockType") {
    schemas.push(
      productObjectSchema(node.quickAttachment.product, node.authors),
    );
  }

  if (node.__typename === "StreamResourceType") {
    node.streamPosts?.nodes.forEach((streamEntry) => {
      if (streamEntry.quickAttachment?.__typename === "ProductBlockType") {
        schemas.push(
          productObjectSchema(
            streamEntry.quickAttachment.product,
            streamEntry.authors,
          ),
        );
      }
    });
  }

  return schemas.map((schema, index) => {
    return <SEOSchema key={index} schema={schema} />;
  });
};

export default SEOSchemasContainer;
